// Anzeige-Namen von Katalog-Größen (Issue #373).
//
// Im Anlagengraph steht überall der technische Bezeichner — `AT-201`, `FQ-107@d60`. Wer die
// Anlage kennt, liest das; wer eine Entscheidung treffen soll, nicht. Diese eine Stelle
// entscheidet, was stattdessen erscheint.
//
// Zwei Regeln, die zusammengehören:
//
//  * **Der Rückfall geht auf den Bezeichner, nie auf leer.** Wo kein Label hinterlegt ist, steht
//    der Tag. Ein Bediener, der statt `AT-201` nichts sieht, ist schlechter dran als einer, der
//    den rohen Bezeichner liest.
//  * **Der Bezeichner verschwindet nicht.** Er steht als Beleg neben dem Label. Wer ein Label für
//    falsch hält, muss nachsehen können, welche Größe wirklich gemeint ist.
//
// Bewusst NICHT im Rückfall: `variable.name`. Der ist der kurze Name aus dem Sensor-Datenmodell
// (`Q`, `COD`, `L_pct`) und damit für eine Anzeige schlechter als der Tag — „Q" sagt einem
// Bediener weniger als „FT-001", weil es in derselben Anlage mehrfach vorkommt.

/** Das Mindeste, was eine anzeigbare Größe mitbringt. */
export interface Beschriftbar {
  tag: string;
  label?: string | null;
}

/** Trimmt ein Label und wirft Leerstrings weg — ein leeres Label ist kein Label. */
function sauber(label: string | null | undefined): string | null {
  if (typeof label !== 'string') return null;
  const t = label.trim();
  return t.length > 0 ? t : null;
}

/**
 * Der anzuzeigende Name einer Größe: das Kurzlabel, sonst der Bezeichner.
 * Gibt garantiert einen nicht-leeren String zurück, solange `tag` gesetzt ist.
 */
export function anzeigeName(v: Beschriftbar): string {
  return sauber(v.label) ?? v.tag;
}

/**
 * Name mit Bezeichner als Beleg: „NH4 Ablauf (AT-201)". Ohne Label bleibt es beim Bezeichner
 * allein — „AT-201 (AT-201)" wäre Lärm.
 *
 * `belegTag` überschreibt den angezeigten Bezeichner für Fälle, in denen der Beleg genauer ist als
 * der Tag der Variable — etwa ein Optimierer-Ausgang `AT-201@d60`, bei dem der Horizont zum Beleg
 * gehört.
 */
export function anzeigeMitBeleg(v: Beschriftbar, belegTag?: string): string {
  const beleg = belegTag ?? v.tag;
  const label = sauber(v.label);
  return label ? `${label} (${beleg})` : beleg;
}
